import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { UserCheck, Loader2, Sparkles, FilePlus2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Lets the admin pick which user a "Sin factura" deposit belongs to and confirm it
export const AssignDepositPanel = ({ deposit, onAssigned }) => {
    const { getAuthHeaders } = useAuth();
    const [users, setUsers] = useState([]);
    const [userId, setUserId] = useState('');
    const [pending, setPending] = useState([]);
    const [invoiceId, setInvoiceId] = useState('');
    const [loadingPending, setLoadingPending] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        axios.get(`${API}/api/users`, { headers: getAuthHeaders() })
            .then(res => setUsers((res.data || []).filter(u => u.role !== 'admin')))
            .catch(() => {});
    }, [getAuthHeaders]);

    useEffect(() => {
        setInvoiceId('');
        setPending([]);
        if (!userId) return;
        setLoadingPending(true);
        axios.get(`${API}/api/admin/user-pending-invoices/${userId}`, { headers: getAuthHeaders() })
            .then(res => {
                const list = res.data.invoices || [];
                setPending(list);
                const exact = list.find(i => Math.abs(i.amount - deposit.amount) < 0.01);
                if (exact) setInvoiceId(exact.id);
            })
            .catch(() => toast.error('Error al cargar facturas pendientes'))
            .finally(() => setLoadingPending(false));
    }, [userId, deposit.amount, getAuthHeaders]);

    const selectedUser = users.find(u => u.id === userId);
    const suggestedNames = (deposit.suggested_users || []).map(s => s.user_name);
    const sorted = [...users].sort((a, b) => {
        const ia = suggestedNames.indexOf(a.name), ib = suggestedNames.indexOf(b.name);
        if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
        return a.name.localeCompare(b.name);
    });

    const assign = async () => {
        setSaving(true);
        try {
            const res = await axios.post(`${API}/api/admin/deposits/assign`, {
                email_id: deposit.email_id, user_id: userId, invoice_id: invoiceId || null,
            }, { headers: getAuthHeaders() });
            toast.success(res.data.message);
            onAssigned && onAssigned(res.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al asignar el depósito');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 space-y-2" data-testid="assign-deposit-panel">
            <p className="font-semibold text-emerald-800 flex items-center gap-1.5 text-sm">
                <UserCheck className="w-4 h-4" /> Asignar este depósito a un usuario
            </p>
            <select
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                className="w-full h-9 rounded-md border border-emerald-200 bg-white px-2 text-sm"
                data-testid="assign-user-select"
            >
                <option value="">Escoge el usuario que depositó…</option>
                {sorted.map(u => (
                    <option key={u.id} value={u.id}>
                        {suggestedNames.includes(u.name) ? '★ ' : ''}{u.name}{u.is_extra ? ' (Extra)' : ''}
                    </option>
                ))}
            </select>

            {userId && (
                <>
                    {loadingPending ? (
                        <p className="text-xs text-slate-500 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Buscando facturas pendientes…</p>
                    ) : (
                        <select
                            value={invoiceId}
                            onChange={(e) => setInvoiceId(e.target.value)}
                            className="w-full h-9 rounded-md border border-emerald-200 bg-white px-2 text-sm"
                            data-testid="assign-invoice-select"
                        >
                            <option value="">＋ Crear factura nueva con los datos del depósito</option>
                            {pending.map(i => (
                                <option key={i.id} value={i.id}>
                                    {i.name} — ${Number(i.amount).toFixed(2)} {i.category ? `(${i.category})` : ''}{Math.abs(i.amount - deposit.amount) < 0.01 ? ' ✓ mismo monto' : ''}
                                </option>
                            ))}
                        </select>
                    )}
                    <p className={`text-xs rounded px-2 py-1 ${selectedUser?.is_extra ? 'bg-fuchsia-100 text-fuchsia-800' : 'bg-white text-slate-600'}`} data-testid="assign-accounting-hint">
                        {selectedUser?.is_extra ? (
                            <><Sparkles className="w-3 h-3 inline mr-1" />Usuario extra: se contabiliza en <b>Contabilidad Extra</b>, no en los totales generales.</>
                        ) : (
                            <>Se contabiliza en la contabilidad general de <b>{selectedUser?.name}</b>.</>
                        )}
                        {!invoiceId && <> Se creará la factura "<b>{deposit.sender_name}</b>" por <b>${Number(deposit.amount).toFixed(2)}</b>.</>}
                    </p>
                    <Button
                        size="sm"
                        onClick={assign}
                        disabled={saving || loadingPending}
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="assign-confirm-btn"
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : invoiceId ? <UserCheck className="w-4 h-4 mr-1" /> : <FilePlus2 className="w-4 h-4 mr-1" />}
                        {saving ? 'Asignando…' : invoiceId ? 'Confirmar factura con este depósito' : 'Crear factura y confirmar'}
                    </Button>
                </>
            )}
        </div>
    );
};
